import { EventLightVm, EventShotLightVm, MatchVm } from '../../utils/api';
import {
    ClubSummary,
    MatchResult,
    summarizeClubMatches,
    toClubMatchView,
} from './insightsClubSummary';
import {
    eventMapPeriodKey,
    formatPeriodLabel,
    MAP_PERIOD_LABELS,
    MapPeriodKey,
} from './insightsMatchStats';
import { MatchBundle } from './insightsQueries';

/*
 * Team level splits a coach asks for first: where the goals come from, which period we
 * fade in, and whether home and away look like the same team. All from logged events, so
 * opponent numbers are "against us" - never a claim about their whole season.
 */

export type AttackType = 'extraPlayer' | 'penalty' | 'counter' | 'regular';

export const ATTACK_LABELS: Record<AttackType, string> = {
    extraPlayer: 'Extra player',
    penalty: 'Penalty',
    counter: 'Counter attack',
    regular: 'Even strength',
};

export const ATTACK_ORDER: AttackType[] = ['extraPlayer', 'penalty', 'counter', 'regular'];

const normalise = (value?: string): string => (value || '').toLowerCase().replace(/[\s_-]/g, '');

/**
 * Shot type names come from the codebook, which is seeded from ShotTypeEnum.ToString()
 * ("Power_Play", "Penalty"). Normalising keeps this working if the labels are ever prettified.
 */
export const classifyShot = (shot: EventShotLightVm): AttackType => {
    const type = normalise(shot.type);
    if (type === 'powerplay') return 'extraPlayer';
    if (type === 'penalty' || type === 'penaltyshootout') return 'penalty';
    if (shot.isFastBreak) return 'counter';
    return 'regular';
};

export interface AttackSplit {
    shots: number;
    goals: number;
}

export type AttackSplits = Record<AttackType, AttackSplit>;

export interface SideSplits {
    us: AttackSplits;
    them: AttackSplits;
}

const emptyAttackSplits = (): AttackSplits => ({
    extraPlayer: { shots: 0, goals: 0 },
    penalty: { shots: 0, goals: 0 },
    counter: { shots: 0, goals: 0 },
    regular: { shots: 0, goals: 0 },
});

export const conversionPercent = (split: AttackSplit): number | undefined =>
    split.shots > 0 ? Math.round((split.goals / split.shots) * 100) : undefined;

export const pctText = (value?: number): string => (value == null ? '-' : `${value}%`);

const rate = (total: number, matches: number): string =>
    matches > 0 ? (total / matches).toFixed(1) : '-';

export const buildAttackSplits = (bundles: MatchBundle[], ourTeamIds: number[]): SideSplits => {
    const result: SideSplits = { us: emptyAttackSplits(), them: emptyAttackSplits() };

    bundles.forEach((bundle: MatchBundle) => {
        bundle.events.forEach((event: EventLightVm) => {
            if (!event.shot) return;
            const side = ourTeamIds.includes(event.shot.teamId) ? result.us : result.them;
            const bucket = side[classifyShot(event.shot)];
            bucket.shots += 1;
            if (event.shot.isGoal) bucket.goals += 1;
        });
    });

    return result;
};

export interface GkSitBucket {
    saves: number;
    goalsAgainst: number;
}

export interface TeamTally {
    matches: number;
    shots: number;
    goals: number;
    shotsAgainst: number;
    goalsAgainst: number;
    saves: number;
    sogFaced: number;
    turnovers: number;
    turnoverTypes: Record<string, number>;
    exclusionsCommitted: number;
    exclusionsDrawn: number;
    blocks: number;
    steals: number;
    swimoffWins: number;
    swimoffs: number;
    timeouts: number;
    cards: number;
    attack: AttackSplits;
    them: AttackSplits;
    gkBySit: Record<AttackType, GkSitBucket>;
}

const emptyGkSit = (): Record<AttackType, GkSitBucket> => ({
    extraPlayer: { saves: 0, goalsAgainst: 0 },
    penalty: { saves: 0, goalsAgainst: 0 },
    counter: { saves: 0, goalsAgainst: 0 },
    regular: { saves: 0, goalsAgainst: 0 },
});

export const emptyTeamTally = (): TeamTally => ({
    matches: 0,
    shots: 0,
    goals: 0,
    shotsAgainst: 0,
    goalsAgainst: 0,
    saves: 0,
    sogFaced: 0,
    turnovers: 0,
    turnoverTypes: {},
    exclusionsCommitted: 0,
    exclusionsDrawn: 0,
    blocks: 0,
    steals: 0,
    swimoffWins: 0,
    swimoffs: 0,
    timeouts: 0,
    cards: 0,
    attack: emptyAttackSplits(),
    them: emptyAttackSplits(),
    gkBySit: emptyGkSit(),
});

const addAttack = (target: AttackSplits, source: AttackSplits): void => {
    ATTACK_ORDER.forEach((type) => {
        target[type].shots += source[type].shots;
        target[type].goals += source[type].goals;
    });
};

export const addTeamTally = (target: TeamTally, source: TeamTally): void => {
    target.matches += source.matches;
    target.shots += source.shots;
    target.goals += source.goals;
    target.shotsAgainst += source.shotsAgainst;
    target.goalsAgainst += source.goalsAgainst;
    target.saves += source.saves;
    target.sogFaced += source.sogFaced;
    target.turnovers += source.turnovers;
    target.exclusionsCommitted += source.exclusionsCommitted;
    target.exclusionsDrawn += source.exclusionsDrawn;
    target.blocks += source.blocks;
    target.steals += source.steals;
    target.swimoffWins += source.swimoffWins;
    target.swimoffs += source.swimoffs;
    target.timeouts += source.timeouts;
    target.cards += source.cards;
    addAttack(target.attack, source.attack);
    addAttack(target.them, source.them);
    ATTACK_ORDER.forEach((type) => {
        target.gkBySit[type].saves += source.gkBySit[type].saves;
        target.gkBySit[type].goalsAgainst += source.gkBySit[type].goalsAgainst;
    });
    Object.keys(source.turnoverTypes).forEach((key) => {
        target.turnoverTypes[key] = (target.turnoverTypes[key] || 0) + source.turnoverTypes[key];
    });
};

const lineupTeamId = (bundle: MatchBundle, matchPlayerId?: number): number | undefined => {
    if (matchPlayerId == null) return undefined;
    const mp = (bundle.detail.players || []).find((p) => p.id === matchPlayerId);
    return mp?.teamId;
};

export const bundleMatchResult = (
    bundle: MatchBundle,
    ourTeamIds: number[]
): MatchResult | undefined => {
    const { detail } = bundle;
    const isHome = ourTeamIds.includes(detail.homeTeamId);
    const isAway = ourTeamIds.includes(detail.awayTeamId);
    if (!isHome && !isAway) return undefined;
    const gf = isHome ? detail.homeTeamGoalsTotal : detail.awayTeamGoalsTotal;
    const ga = isHome ? detail.awayTeamGoalsTotal : detail.homeTeamGoalsTotal;
    if (gf == null || ga == null) return undefined;
    if (gf > ga) return 'W';
    if (gf < ga) return 'L';
    return 'D';
};

export const tallyOneMatch = (bundle: MatchBundle, ourTeamIds: number[]): TeamTally => {
    const tally = emptyTeamTally();
    tally.matches = 1;

    bundle.events.forEach((event: EventLightVm) => {
        if (event.shot) {
            const sit = classifyShot(event.shot);
            const ours = ourTeamIds.includes(event.shot.teamId);
            if (ours) {
                tally.shots += 1;
                tally.attack[sit].shots += 1;
                if (event.shot.isGoal) {
                    tally.goals += 1;
                    tally.attack[sit].goals += 1;
                }
            } else {
                tally.shotsAgainst += 1;
                tally.them[sit].shots += 1;
                if (event.shot.isGoal) {
                    tally.goalsAgainst += 1;
                    tally.them[sit].goals += 1;
                    tally.sogFaced += 1;
                    tally.gkBySit[sit].goalsAgainst += 1;
                } else if (event.shot.savedById != null) {
                    const saverTeam = lineupTeamId(bundle, event.shot.savedById);
                    if (saverTeam == null || ourTeamIds.includes(saverTeam)) {
                        tally.saves += 1;
                        tally.sogFaced += 1;
                        tally.gkBySit[sit].saves += 1;
                    }
                }
            }
            if (event.shot.blockedById != null) {
                const blockerTeam = lineupTeamId(bundle, event.shot.blockedById);
                if (blockerTeam != null && ourTeamIds.includes(blockerTeam)) tally.blocks += 1;
            }
            return;
        }
        if (event.exclusion) {
            if (ourTeamIds.includes(event.exclusion.teamId)) tally.exclusionsCommitted += 1;
            else tally.exclusionsDrawn += 1;
            return;
        }
        if (event.turnover) {
            const typeKey = event.turnover.type || 'Unknown';
            if (ourTeamIds.includes(event.turnover.teamId)) {
                tally.turnovers += 1;
                tally.turnoverTypes[typeKey] = (tally.turnoverTypes[typeKey] || 0) + 1;
            } else {
                tally.steals += 1;
            }
            return;
        }
        if (event.swimoff) {
            tally.swimoffs += 1;
            if (
                event.swimoff.winnerTeamId != null &&
                ourTeamIds.includes(event.swimoff.winnerTeamId)
            ) {
                tally.swimoffWins += 1;
            }
            return;
        }
        if (event.timeout && ourTeamIds.includes(event.timeout.teamId)) tally.timeouts += 1;
        if (event.card && ourTeamIds.includes(event.card.teamId)) tally.cards += 1;
    });

    return tally;
};

export const tallyBundles = (bundles: MatchBundle[], ourTeamIds: number[]): TeamTally => {
    const tally = emptyTeamTally();
    bundles.forEach((bundle) => addTeamTally(tally, tallyOneMatch(bundle, ourTeamIds)));
    return tally;
};

export const shootingPct = (tally: TeamTally): number | undefined =>
    tally.shots > 0 ? Math.round((tally.goals / tally.shots) * 100) : undefined;

export const savePct = (tally: TeamTally): number | undefined =>
    tally.sogFaced > 0 ? Math.round((tally.saves / tally.sogFaced) * 100) : undefined;

export const gkSitPct = (bucket: GkSitBucket): number | undefined => {
    const faced = bucket.saves + bucket.goalsAgainst;
    return faced > 0 ? Math.round((bucket.saves / faced) * 100) : undefined;
};

export const pkPct = (tally: TeamTally): number | undefined => {
    const opp = conversionPercent(tally.them.extraPlayer);
    return opp == null ? undefined : 100 - opp;
};

const TURNOVER_TYPE_LABELS: Record<string, string> = {
    lostball: 'Lost ball',
    offensivefoul: 'Offensive foul',
    ballunder: 'Ball under',
    clockexpired: 'Clock expired',
};

export const turnoverTypeLabel = (type: string): string => {
    const key = normalise(type);
    if (TURNOVER_TYPE_LABELS[key]) return TURNOVER_TYPE_LABELS[key];
    return type.replace(/_/g, ' ');
};

export interface PeriodSplit {
    period: string;
    periodId: number;
    goalsFor: number;
    goalsAgainst: number;
    shotsFor: number;
    shotsAgainst: number;
    turnoversFor: number;
    exclusionsCommitted: number;
    exclusionsDrawn: number;
    extraPlayerShots: number;
    extraPlayerGoals: number;
}

const emptyPeriod = (periodId: number, period: string): PeriodSplit => ({
    period,
    periodId,
    goalsFor: 0,
    goalsAgainst: 0,
    shotsFor: 0,
    shotsAgainst: 0,
    turnoversFor: 0,
    exclusionsCommitted: 0,
    exclusionsDrawn: 0,
    extraPlayerShots: 0,
    extraPlayerGoals: 0,
});

/** Period box across the sample: goals plus the stuff that explains a fade. */
export const buildPeriodSplits = (bundles: MatchBundle[], ourTeamIds: number[]): PeriodSplit[] => {
    const byPeriod = new Map<number, PeriodSplit>();

    const ensure = (event: EventLightVm): PeriodSplit => {
        const existing = byPeriod.get(event.periodId);
        if (existing) return existing;
        const key = eventMapPeriodKey(event);
        const label = key
            ? MAP_PERIOD_LABELS[key]
            : formatPeriodLabel(event.period, event.periodId) || `P${event.periodId}`;
        const row = emptyPeriod(event.periodId, label);
        byPeriod.set(event.periodId, row);
        return row;
    };

    bundles.forEach((bundle: MatchBundle) => {
        bundle.events.forEach((event: EventLightVm) => {
            if (event.shot) {
                const row = ensure(event);
                const sit = classifyShot(event.shot);
                const ours = ourTeamIds.includes(event.shot.teamId);
                if (ours) {
                    row.shotsFor += 1;
                    if (event.shot.isGoal) row.goalsFor += 1;
                    if (sit === 'extraPlayer') {
                        row.extraPlayerShots += 1;
                        if (event.shot.isGoal) row.extraPlayerGoals += 1;
                    }
                } else {
                    row.shotsAgainst += 1;
                    if (event.shot.isGoal) row.goalsAgainst += 1;
                }
                return;
            }
            if (event.exclusion) {
                const row = ensure(event);
                if (ourTeamIds.includes(event.exclusion.teamId)) row.exclusionsCommitted += 1;
                else row.exclusionsDrawn += 1;
                return;
            }
            if (event.turnover && ourTeamIds.includes(event.turnover.teamId)) {
                ensure(event).turnoversFor += 1;
            }
        });
    });

    return Array.from(byPeriod.entries())
        .sort((a, b) => a[0] - b[0])
        .map((entry) => entry[1]);
};

export interface WdlCount {
    w: number;
    d: number;
    l: number;
}

const emptyWdl = (): WdlCount => ({ w: 0, d: 0, l: 0 });

const bumpWdl = (row: WdlCount, result?: MatchResult): void => {
    if (result === 'W') row.w += 1;
    else if (result === 'D') row.d += 1;
    else if (result === 'L') row.l += 1;
};

export const wdlText = (row: WdlCount): string => `${row.w}-${row.d}-${row.l}`;

export interface QuarterControlRow {
    label: string;
    played: number;
    won: number;
    drawn: number;
    lost: number;
    whenWon: WdlCount;
    whenLost: WdlCount;
    whenDrawn: WdlCount;
}

const QUARTER_KEYS: MapPeriodKey[] = ['q1', 'q2', 'q3', 'q4'];

/** Match W-D-L when we won / lost / drew that quarter (event goals in the period). */
export const buildQuarterControl = (
    bundles: MatchBundle[],
    ourTeamIds: number[]
): QuarterControlRow[] => {
    const rows = QUARTER_KEYS.map((key) => ({
        label: MAP_PERIOD_LABELS[key],
        key,
        played: 0,
        won: 0,
        drawn: 0,
        lost: 0,
        whenWon: emptyWdl(),
        whenLost: emptyWdl(),
        whenDrawn: emptyWdl(),
    }));
    const byKey = new Map(rows.map((row) => [row.key, row]));

    bundles.forEach((bundle) => {
        const matchResult = bundleMatchResult(bundle, ourTeamIds);
        const periodGoals = new Map<MapPeriodKey, { gf: number; ga: number }>();
        bundle.events.forEach((event) => {
            if (!event.shot || !event.shot.isGoal) return;
            const key = eventMapPeriodKey(event);
            if (key == null || !QUARTER_KEYS.includes(key)) return;
            const slot = periodGoals.get(key) || { gf: 0, ga: 0 };
            if (ourTeamIds.includes(event.shot.teamId)) slot.gf += 1;
            else slot.ga += 1;
            periodGoals.set(key, slot);
        });

        periodGoals.forEach((slot, key) => {
            const row = byKey.get(key);
            if (!row) return;
            row.played += 1;
            const q: MatchResult = slot.gf > slot.ga ? 'W' : slot.gf < slot.ga ? 'L' : 'D';
            if (q === 'W') {
                row.won += 1;
                bumpWdl(row.whenWon, matchResult);
            } else if (q === 'L') {
                row.lost += 1;
                bumpWdl(row.whenLost, matchResult);
            } else {
                row.drawn += 1;
                bumpWdl(row.whenDrawn, matchResult);
            }
        });
    });

    return rows.filter((row) => row.played > 0).map(({ key: _key, ...rest }) => rest);
};

export interface VenueSplits {
    home: ClubSummary;
    away: ClubSummary;
}

export const buildVenueSplits = (matches: MatchVm[], teamIds: number[]): VenueSplits => ({
    home: summarizeClubMatches(
        matches.filter((match: MatchVm) => teamIds.includes(match.homeTeamId)),
        teamIds
    ),
    away: summarizeClubMatches(
        matches.filter((match: MatchVm) => teamIds.includes(match.awayTeamId)),
        teamIds
    ),
});

export interface CompareRow {
    label: string;
    left: string;
    right: string;
}

const tallyCompareRows = (left: TeamTally, right: TeamTally): CompareRow[] => {
    const nL = left.matches || 1;
    const nR = right.matches || 1;
    return [
        { label: 'Matches', left: `${left.matches}`, right: `${right.matches}` },
        {
            label: 'Shooting %',
            left: pctText(shootingPct(left)),
            right: pctText(shootingPct(right)),
        },
        {
            label: 'Extra player %',
            left: pctText(conversionPercent(left.attack.extraPlayer)),
            right: pctText(conversionPercent(right.attack.extraPlayer)),
        },
        { label: 'Penalty kill %', left: pctText(pkPct(left)), right: pctText(pkPct(right)) },
        {
            label: '6v6 %',
            left: pctText(conversionPercent(left.attack.regular)),
            right: pctText(conversionPercent(right.attack.regular)),
        },
        {
            label: 'Counter goals / g',
            left: rate(left.attack.counter.goals, nL),
            right: rate(right.attack.counter.goals, nR),
        },
        { label: 'Save %', left: pctText(savePct(left)), right: pctText(savePct(right)) },
        {
            label: 'Turnovers / g',
            left: rate(left.turnovers, nL),
            right: rate(right.turnovers, nR),
        },
        { label: 'Steals / g', left: rate(left.steals, nL), right: rate(right.steals, nR) },
        { label: 'Blocks / g', left: rate(left.blocks, nL), right: rate(right.blocks, nR) },
        {
            label: 'Ex net / g',
            left: rate(left.exclusionsDrawn - left.exclusionsCommitted, nL),
            right: rate(right.exclusionsDrawn - right.exclusionsCommitted, nR),
        },
        {
            label: 'Swim-off',
            left: `${left.swimoffWins}/${left.swimoffs}`,
            right: `${right.swimoffWins}/${right.swimoffs}`,
        },
        { label: 'Goals / g', left: rate(left.goals, nL), right: rate(right.goals, nR) },
        {
            label: 'Conceded / g',
            left: rate(left.goalsAgainst, nL),
            right: rate(right.goalsAgainst, nR),
        },
    ];
};

export interface WhyWeWinRow {
    label: string;
    wins: string;
    losses: string;
}

/** Compare our event rates in wins vs losses (sample bundles). */
export const buildWhyWeWin = (bundles: MatchBundle[], ourTeamIds: number[]): WhyWeWinRow[] => {
    const winBundles: MatchBundle[] = [];
    const lossBundles: MatchBundle[] = [];

    bundles.forEach((bundle) => {
        const result = bundleMatchResult(bundle, ourTeamIds);
        if (result === 'W') winBundles.push(bundle);
        else if (result === 'L') lossBundles.push(bundle);
    });

    if (winBundles.length === 0 && lossBundles.length === 0) return [];

    return tallyCompareRows(
        tallyBundles(winBundles, ourTeamIds),
        tallyBundles(lossBundles, ourTeamIds)
    ).map((row) => ({ label: row.label, wins: row.left, losses: row.right }));
};

export const buildVenueEventRows = (bundles: MatchBundle[], ourTeamIds: number[]): CompareRow[] => {
    const home = bundles.filter((b) => ourTeamIds.includes(b.detail.homeTeamId));
    const away = bundles.filter((b) => ourTeamIds.includes(b.detail.awayTeamId));
    if (home.length === 0 && away.length === 0) return [];
    return tallyCompareRows(tallyBundles(home, ourTeamIds), tallyBundles(away, ourTeamIds));
};

/** Same meetings, our club tally vs opponent tally (H2H prep). */
export const buildUsVsThem = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    opponentTeamId: number
): CompareRow[] => {
    if (bundles.length === 0) return [];
    return tallyCompareRows(
        tallyBundles(bundles, ourTeamIds),
        tallyBundles(bundles, [opponentTeamId])
    );
};

export interface TurnoverTypeRow {
    label: string;
    count: number;
}

export const buildTurnoverTypeRows = (tally: TeamTally): TurnoverTypeRow[] =>
    Object.keys(tally.turnoverTypes)
        .sort((a, b) => tally.turnoverTypes[b] - tally.turnoverTypes[a])
        .map((type) => ({ label: turnoverTypeLabel(type), count: tally.turnoverTypes[type] }));

export interface TeamScoreForm {
    last10: MatchResult[];
    streak: string;
    closePlayed: number;
    closeWins: number;
    closeDraws: number;
    closeLosses: number;
    avgWinMargin?: number;
    avgLossMargin?: number;
}

const streakText = (form: MatchResult[]): string => {
    if (form.length === 0) return '-';
    const first = form[0];
    let n = 0;
    for (const r of form) {
        if (r !== first) break;
        n += 1;
    }
    return `${first}${n}`;
};

export const buildTeamScoreForm = (matches: MatchVm[], teamIds: number[]): TeamScoreForm => {
    const results: MatchResult[] = [];
    const winMargins: number[] = [];
    const lossMargins: number[] = [];
    let closePlayed = 0;
    let closeWins = 0;
    let closeDraws = 0;
    let closeLosses = 0;

    matches.forEach((match) => {
        const view = toClubMatchView(match, teamIds);
        if (!view || !view.result || view.goalsFor == null || view.goalsAgainst == null) return;
        results.push(view.result);
        const margin = view.goalsFor - view.goalsAgainst;
        if (view.result === 'W') winMargins.push(margin);
        if (view.result === 'L') lossMargins.push(-margin);
        if (Math.abs(margin) <= 1) {
            closePlayed += 1;
            if (view.result === 'W') closeWins += 1;
            else if (view.result === 'D') closeDraws += 1;
            else closeLosses += 1;
        }
    });

    const avg = (xs: number[]): number | undefined =>
        xs.length > 0 ? xs.reduce((s, v) => s + v, 0) / xs.length : undefined;

    return {
        last10: results.slice(0, 10),
        streak: streakText(results),
        closePlayed,
        closeWins,
        closeDraws,
        closeLosses,
        avgWinMargin: avg(winMargins),
        avgLossMargin: avg(lossMargins),
    };
};

export interface SampleFormRow {
    label: string;
    sample: string;
    recent: string;
    delta: number;
    lowerIsBetter?: boolean;
}

const formDelta = (
    label: string,
    sample?: number,
    recent?: number,
    lowerIsBetter?: boolean,
    suffix = ''
): SampleFormRow | undefined => {
    if (sample == null && recent == null) return undefined;
    const s = sample ?? 0;
    const r = recent ?? 0;
    return {
        label,
        sample: sample == null ? '-' : `${sample}${suffix}`,
        recent: recent == null ? '-' : `${recent}${suffix}`,
        delta: r - s,
        lowerIsBetter,
    };
};

/** Last 10 analysed matches vs the filtered sample (pooled, not avg of avgs). */
export const buildRecentVsSample = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    recentCount = 10
): SampleFormRow[] => {
    if (bundles.length < 4) return [];
    const recent = tallyBundles(bundles.slice(0, recentCount), ourTeamIds);
    const sample = tallyBundles(bundles, ourTeamIds);
    const nS = sample.matches || 1;
    const nR = recent.matches || 1;
    return [
        formDelta('Shooting %', shootingPct(sample), shootingPct(recent), false, '%'),
        formDelta(
            'Extra player %',
            conversionPercent(sample.attack.extraPlayer),
            conversionPercent(recent.attack.extraPlayer),
            false,
            '%'
        ),
        formDelta('Penalty kill %', pkPct(sample), pkPct(recent), false, '%'),
        formDelta('Save %', savePct(sample), savePct(recent), false, '%'),
        formDelta('Goals / g', sample.goals / nS, recent.goals / nR),
        formDelta('Conceded / g', sample.goalsAgainst / nS, recent.goalsAgainst / nR, true),
        formDelta('Turnovers / g', sample.turnovers / nS, recent.turnovers / nR, true),
    ].filter((row): row is SampleFormRow => row != null);
};

export type TeamTrendMetric =
    | 'shootingPct'
    | 'extraPlayerPct'
    | 'penaltyKillPct'
    | 'savePct'
    | 'goals'
    | 'goalsAgainst'
    | 'turnovers';

export const TEAM_TREND_METRICS: { value: TeamTrendMetric; label: string }[] = [
    { value: 'shootingPct', label: 'Shooting %' },
    { value: 'extraPlayerPct', label: 'EP %' },
    { value: 'penaltyKillPct', label: 'PK %' },
    { value: 'savePct', label: 'Save %' },
    { value: 'goals', label: 'Goals' },
    { value: 'goalsAgainst', label: 'Conceded' },
    { value: 'turnovers', label: 'Turnovers' },
];

export const isPercentTrend = (metric: TeamTrendMetric): boolean =>
    metric === 'shootingPct' ||
    metric === 'extraPlayerPct' ||
    metric === 'penaltyKillPct' ||
    metric === 'savePct';

const pickTrend = (tally: TeamTally, metric: TeamTrendMetric): number => {
    switch (metric) {
        case 'shootingPct':
            return shootingPct(tally) ?? 0;
        case 'extraPlayerPct':
            return conversionPercent(tally.attack.extraPlayer) ?? 0;
        case 'penaltyKillPct':
            return pkPct(tally) ?? 0;
        case 'savePct':
            return savePct(tally) ?? 0;
        case 'goals':
            return tally.goals;
        case 'goalsAgainst':
            return tally.goalsAgainst;
        case 'turnovers':
            return tally.turnovers;
        default:
            return 0;
    }
};

export interface TeamTrendPoint {
    matchId: number;
    label: string;
    value: number;
    opponentName?: string;
    when?: string;
    resultLine?: string;
}

const shortDate = (value?: Date): string | undefined => {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/** Chronological ascending. Input bundles are newest-first. */
export const buildTeamTrendSeries = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    metric: TeamTrendMetric
): TeamTrendPoint[] =>
    [...bundles].reverse().map((bundle, index) => {
        const { detail } = bundle;
        const isHome = ourTeamIds.includes(detail.homeTeamId);
        const gf = isHome ? detail.homeTeamGoalsTotal : detail.awayTeamGoalsTotal;
        const ga = isHome ? detail.awayTeamGoalsTotal : detail.homeTeamGoalsTotal;
        const result = bundleMatchResult(bundle, ourTeamIds);
        return {
            matchId: detail.id,
            label: `R${index + 1}`,
            value: pickTrend(tallyOneMatch(bundle, ourTeamIds), metric),
            opponentName:
                (isHome ? detail.awayTeamDisplayName : detail.homeTeamDisplayName) || undefined,
            when: shortDate(detail.startDate),
            resultLine: result && gf != null && ga != null ? `${result} ${gf}:${ga}` : result,
        };
    });

export interface CorrelateRow {
    label: string;
    high: string;
    low: string;
    nHigh: number;
    nLow: number;
}

const median = (values: number[]): number | undefined => {
    if (values.length === 0) return undefined;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

const wdlFromResults = (results: MatchResult[]): string => {
    const row = emptyWdl();
    results.forEach((r) => bumpWdl(row, r));
    return wdlText(row);
};

/** Win-D-L when a per-match metric is at/above vs below the sample median. */
export const buildWinCorrelates = (
    bundles: MatchBundle[],
    ourTeamIds: number[]
): CorrelateRow[] => {
    const lines = bundles
        .map((bundle) => {
            const result = bundleMatchResult(bundle, ourTeamIds);
            if (!result) return undefined;
            return { result, tally: tallyOneMatch(bundle, ourTeamIds) };
        })
        .filter((line): line is { result: MatchResult; tally: TeamTally } => line != null);

    if (lines.length < 4) return [];

    const metrics: { label: string; pick: (t: TeamTally) => number | undefined }[] = [
        { label: 'Shooting %', pick: shootingPct },
        { label: 'Extra player %', pick: (t) => conversionPercent(t.attack.extraPlayer) },
        { label: 'Penalty kill %', pick: pkPct },
        { label: 'Save %', pick: savePct },
        { label: 'Turnovers', pick: (t) => t.turnovers },
        { label: 'Counter goals', pick: (t) => t.attack.counter.goals },
        { label: 'Ex net', pick: (t) => t.exclusionsDrawn - t.exclusionsCommitted },
        { label: 'Swim-off wins', pick: (t) => (t.swimoffs > 0 ? t.swimoffWins : undefined) },
    ];

    const rows: CorrelateRow[] = [];
    metrics.forEach((metric) => {
        const valued = lines
            .map((line) => ({ result: line.result, value: metric.pick(line.tally) }))
            .filter((x): x is { result: MatchResult; value: number } => x.value != null);
        if (valued.length < 4) return;
        const mid = median(valued.map((x) => x.value));
        if (mid == null) return;
        const high = valued.filter((x) => x.value >= mid).map((x) => x.result);
        const low = valued.filter((x) => x.value < mid).map((x) => x.result);
        if (high.length === 0 || low.length === 0) return;
        rows.push({
            label: metric.label,
            high: wdlFromResults(high),
            low: wdlFromResults(low),
            nHigh: high.length,
            nLow: low.length,
        });
    });
    return rows;
};
